/**
 * @file ResizableImageExtension.js
 * @description Image node that keeps the pixel width it was inserted or resized to, so saved
 * content renders at that fixed size instead of stretching to the container. Resizing uses
 * TipTap v3's built-in resizable node view, and widths/alignment saved by the former
 * tiptap-extension-resize-image markup (style / containerstyle attributes) are still read.
 * Alignment (left by default, center, right) is stored as the image margin.
 */
import Image from '@tiptap/extension-image';
import { getText } from '../../i18n';

const IMAGE_ALIGNMENTS = {
    left: {
        margin: '0 auto 0 0',
        iconClassName: 'bi-text-left',
        labelKey: 'msg_9fb06a282f72',
    },
    center: {
        margin: '0 auto',
        iconClassName: 'bi-text-center',
        labelKey: 'msg_472e0dd9e265',
    },
    right: {
        margin: '0 0 0 auto',
        iconClassName: 'bi-text-right',
        labelKey: 'msg_2e324ba22982',
    },
};
const JUSTIFICATION_ALIGNMENTS = {
    'flex-start': 'left',
    center: 'center',
    'flex-end': 'right',
};

const PIXEL_WIDTH_PATTERN = /(?:^|;)\s*width:\s*([0-9.]+)px/i;
const MARGIN_PATTERN = /(?:^|;)\s*margin:\s*([^;]+)/i;
const MARGIN_TOKEN_PATTERN = /^(?:auto|0|-?\d+(?:\.\d+)?(?:px|%|em|rem))$/i;
const LEGACY_STYLE_ATTRIBUTES = ['style', 'containerstyle'];
const MAX_MARGIN_TOKENS = 4;

export const EDITOR_IMAGE_RESIZE_OPTIONS = {
    enabled: true,
    directions: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
    minWidth: 40,
    minHeight: 20,
    alwaysPreserveAspectRatio: true,
};

export function parsePixelWidth(value) {
    if (typeof value === 'string' && !/^\s*[0-9.]+\s*(px)?\s*$/i.test(value)) {
        return null;
    }

    const width = Math.round(parseFloat(value));
    return Number.isFinite(width) && width > 0 ? width : null;
}

export function parseImageMarginValue(value) {
    if (typeof value !== 'string') {
        return null;
    }

    const tokens = value.trim().split(/\s+/);
    const isSafeMargin =
        tokens.length > 0 &&
        tokens.length <= MAX_MARGIN_TOKENS &&
        tokens.every(token => MARGIN_TOKEN_PATTERN.test(token));

    return isSafeMargin ? tokens.join(' ') : null;
}

function readLegacyStyleValue(element, pattern) {
    return (
        LEGACY_STYLE_ATTRIBUTES.map(
            attributeName =>
                element.getAttribute(attributeName)?.match(pattern)?.[1],
        ).find(Boolean) ?? null
    );
}

function parseImageWidth(element) {
    return (
        parsePixelWidth(readLegacyStyleValue(element, PIXEL_WIDTH_PATTERN)) ??
        parsePixelWidth(element.getAttribute('width'))
    );
}

function parseImageMargin(element) {
    return parseImageMarginValue(readLegacyStyleValue(element, MARGIN_PATTERN));
}

function buildImageStyle({ width, margin }) {
    const declarations = [
        width ? `width: ${width}px` : null,
        'height: auto',
        'display: block',
        margin ? `margin: ${margin}` : null,
    ];

    return `${declarations.filter(Boolean).join('; ')};`;
}

function getMarginJustification(margin) {
    const tokens = margin ? margin.split(' ') : [];
    const [, right = tokens[0], , left = right] = tokens;
    const isRightAuto = right === 'auto';
    const isLeftAuto = left === 'auto';

    if (isLeftAuto && isRightAuto) {
        return 'center';
    }

    return isLeftAuto ? 'flex-end' : 'flex-start';
}

function createAlignmentButton(alignment, onSelect) {
    const { iconClassName, labelKey } = IMAGE_ALIGNMENTS[alignment];
    const button = document.createElement('button');
    const icon = document.createElement('i');

    button.type = 'button';
    button.dataset.imageAlign = alignment;
    button.title = getText(labelKey);
    button.setAttribute('aria-label', getText(labelKey));
    icon.className = `bi ${iconClassName}`;
    button.appendChild(icon);

    button.addEventListener('mousedown', event => {
        event.preventDefault();
        event.stopPropagation();
    });
    button.addEventListener('click', event => {
        event.preventDefault();
        event.stopPropagation();
        onSelect(alignment);
    });

    return button;
}

function createAlignmentControls(nodeView, { editor, getPos }) {
    const controls = document.createElement('div');
    controls.dataset.imageAlignControls = '';
    controls.contentEditable = 'false';

    function selectAlignment(alignment) {
        const position = typeof getPos === 'function' ? getPos() : undefined;
        if (position === undefined || !editor.isEditable) {
            return;
        }

        editor
            .chain()
            .setNodeSelection(position)
            .setImageAlignment(alignment)
            .run();
    }

    Object.keys(IMAGE_ALIGNMENTS).forEach(alignment => {
        controls.appendChild(createAlignmentButton(alignment, selectAlignment));
    });

    nodeView.wrapper.appendChild(controls);
    return controls;
}

// The editor stylesheet zeroes div margins, so alignment is applied through the flex container.
function applyEditorLayout(nodeView, attrs, alignmentControls) {
    const { element } = nodeView;
    const justification = getMarginJustification(attrs.margin);
    const activeAlignment = JUSTIFICATION_ALIGNMENTS[justification];

    element.style.height = 'auto';
    element.style.margin = '0';
    if (attrs.width) {
        element.style.width = `${attrs.width}px`;
    }
    nodeView.dom.style.justifyContent = justification;

    alignmentControls
        ?.querySelectorAll('[data-image-align]')
        .forEach(button => {
            const isActive = button.dataset.imageAlign === activeAlignment;
            button.classList.toggle('active', isActive);
            button.setAttribute('aria-pressed', String(isActive));
        });
}

// Images inserted without a width keep the size they are first displayed at.
function lockDisplayedWidth({ nodeView, node, editor, getPos }) {
    if (node.attrs.width || !editor.isEditable) {
        return;
    }

    const { element } = nodeView;

    function commitDisplayedWidth() {
        const position = typeof getPos === 'function' ? getPos() : undefined;
        if (position === undefined || editor.isDestroyed) {
            return;
        }

        const currentNode = editor.state.doc.nodeAt(position);
        if (currentNode?.type !== node.type || currentNode.attrs.width) {
            return;
        }

        const editorStyle = window.getComputedStyle(editor.view.dom);
        const availableWidth =
            editor.view.dom.clientWidth -
            (parseFloat(editorStyle.paddingLeft) || 0) -
            (parseFloat(editorStyle.paddingRight) || 0);
        const width = parsePixelWidth(
            availableWidth > 0
                ? Math.min(element.naturalWidth, availableWidth)
                : element.naturalWidth,
        );
        if (!width) {
            return;
        }

        const transaction = editor.state.tr
            .setNodeMarkup(position, undefined, {
                ...currentNode.attrs,
                width,
            })
            .setMeta('addToHistory', false);
        editor.view.dispatch(transaction);
    }

    function scheduleCommit() {
        requestAnimationFrame(commitDisplayedWidth);
    }

    if (element.complete && element.naturalWidth > 0) {
        scheduleCommit();
        return;
    }

    element.addEventListener('load', scheduleCommit, { once: true });
}

const ResizableImage = Image.extend({
    addOptions() {
        return {
            ...this.parent?.(),
            allowBase64: true,
        };
    },

    addAttributes() {
        return {
            ...this.parent?.(),
            width: {
                default: null,
                parseHTML: parseImageWidth,
                renderHTML: attributes => ({
                    ...(attributes.width ? { width: attributes.width } : {}),
                    style: buildImageStyle(attributes),
                }),
            },
            height: {
                default: null,
                parseHTML: () => null,
                renderHTML: () => ({}),
            },
            margin: {
                default: null,
                parseHTML: parseImageMargin,
                renderHTML: () => ({}),
            },
        };
    },

    addCommands() {
        return {
            ...this.parent?.(),
            setImageAlignment:
                alignment =>
                ({ commands }) =>
                    Object.hasOwn(IMAGE_ALIGNMENTS, alignment) &&
                    commands.updateAttributes(this.name, {
                        margin: IMAGE_ALIGNMENTS[alignment].margin,
                    }),
        };
    },

    addNodeView() {
        const createResizableNodeView = this.parent?.();
        if (!createResizableNodeView) {
            return null;
        }

        return props => {
            const nodeView = createResizableNodeView(props);
            const updateResizableNodeView = nodeView.update.bind(nodeView);
            const alignmentControls = createAlignmentControls(nodeView, props);

            applyEditorLayout(nodeView, props.node.attrs, alignmentControls);
            lockDisplayedWidth({ ...props, nodeView });

            nodeView.stopEvent = event =>
                Boolean(event.target?.closest?.('[data-image-align-controls]'));
            nodeView.update = (updatedNode, ...updateArgs) => {
                const isUpdated = updateResizableNodeView(
                    updatedNode,
                    ...updateArgs,
                );
                if (isUpdated) {
                    applyEditorLayout(
                        nodeView,
                        updatedNode.attrs,
                        alignmentControls,
                    );
                }
                return isUpdated;
            };

            return nodeView;
        };
    },
});

export default ResizableImage;
